/**
 * Ghost button linking back to the public landing page (or another route),
 * or stepping back in the SPA history when {@link useHistoryBack} is set.
 *
 * @module BackToLandingLink
 */

import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/lib/routeConstants';

interface BackToLandingLinkProps {
  /** Destination path; defaults to the landing page. Ignored when `useHistoryBack` is true. */
  readonly to?: string;
  /**
   * When true, uses router history back (`navigate(-1)`) instead of linking to `to`.
   * Use on flows like Sign Up where the previous screen may be Sign In, not home.
   */
  readonly useHistoryBack?: boolean;
}

export function BackToLandingLink({
  to = ROUTES.LANDING,
  useHistoryBack = false,
}: BackToLandingLinkProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const label = t('common.back', { defaultValue: 'Back' });

  const className =
    'gap-2 px-2 font-ui text-muted-foreground hover:text-foreground';

  if (useHistoryBack) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={className}
        onClick={() => navigate(-1)}
        aria-label={label}
      >
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        {label}
      </Button>
    );
  }

  return (
    <Button asChild variant="ghost" size="sm" className={className}>
      <Link to={to}>
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        {label}
      </Link>
    </Button>
  );
}
